import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { EditIcon, TrashIcon } from 'lucide-react';
import { ConfirmationDialog } from '@/components/custom/confirmation-dialog';
import { DataTable, type Column } from '@/components/custom/data-table';
import { usersApi, tenantsApi } from '@/lib/api';
import type { User, Tenant } from '@/types/entities';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { PageHeader, PageSection } from '@/components/layouts/page-layout';

export function AdminUsersPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | undefined>();
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [usersData, tenantsData] = await Promise.all([
        usersApi.getAll(),
        tenantsApi.getAll(),
      ]);
      setUsers(usersData);
      setTenants(tenantsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = () => {
    navigate('/admin/dashboard/users/create');
  };

  const handleEdit = (user: User) => {
    navigate(`/admin/dashboard/users/${user.id}/edit`);
  };

  const handleDelete = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await usersApi.delete(userToDelete.id);
      setDeleteDialogOpen(false);
      setUserToDelete(undefined);
      await fetchData();
      toast.success('User deleted successfully');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user';
      setError(message);
      toast.error(message);
    }
  };


  const getTenantName = (tenantId: number) => {
    const tenant = tenants.find((t) => t.id === tenantId);
    return tenant?.name || `Tenant #${tenantId}`;
  };

  const columns: Column<User>[] = [
    {
      key: 'id',
      header: 'ID',
      cell: (user) => <span className="text-muted-foreground">#{user.id}</span>,
      className: 'w-20',
      sortable: true,
      sortValue: (user) => user.id,
    },
    {
      key: 'email',
      header: 'Email',
      cell: (user) => user.email,
      sortable: true,
      sortValue: (user) => user.email,
    },
    {
      key: 'display_name',
      header: 'Display Name',
      cell: (user) => user.display_name,
      sortable: true,
      sortValue: (user) => user.display_name,
    },
    {
      key: 'tenant',
      header: 'Tenant',
      cell: (user) => getTenantName(user.tenant_id),
      sortable: true,
      sortValue: (user) => getTenantName(user.tenant_id),
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (user) => new Date(user.created_at).toLocaleDateString(),
      className: 'w-32',
      sortable: true,
      sortValue: (user) => new Date(user.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      cell: (user) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleEdit(user)}
            className="h-8 w-8"
          >
            <EditIcon className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleDelete(user)}
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <TrashIcon className="size-4" />
          </Button>
        </div>
      ),
      className: 'w-24',
    },
  ];

  return (
    <>
      <PageHeader
        title="Users"
        description="Manage tenant users"
      />

      <PageSection>
        {error && (
          <div className="rounded-md bg-destructive/10 p-3 text-destructive">
            {error}
          </div>
        )}

        <DataTable
          data={users}
          columns={columns}
          searchKeys={['email', 'display_name']}
          searchPlaceholder="Search by email or name..."
          onCreate={handleCreate}
          createLabel="Add User"
          emptyMessage="No users found"
          isLoading={isLoading}
        />
      </PageSection>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete User"
        description={`Are you sure you want to delete "${userToDelete?.email}"? This action cannot be undone.`}
        onConfirm={confirmDelete}
        confirmVariant="destructive"
        confirmLabel="Delete"
      />
    </>
  );
}
